import { AdvancedMarker } from "@vis.gl/react-google-maps";
import { memo, useCallback, useEffect, useRef } from "react";
import { MapPin } from "lucide-react";

const PIL_COLOR_BY_STATUS: Record<string, string> = {
  pending: "#9ca3af",
  in_progress: "#f59e0b",
  done: "#10b981",
};

export function getPILColor(status: string): string {
  return PIL_COLOR_BY_STATUS[status] ?? "#9ca3af";
}

interface PILMarkerProps {
  pil: {
    id: string;
    lat: number;
    lng: number;
    ordinal: number;
    status: string;
  };
  isSelected?: boolean;
  isDraggable?: boolean;
  onDragEnd?: (lat: number, lng: number) => void;
  onClick?: (pilId: string) => void;
}

export const PILMarker = memo(function PILMarker({
  pil,
  isSelected,
  isDraggable,
  onDragEnd,
  onClick,
}: PILMarkerProps) {
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const color = getPILColor(pil.status);

  const handleClick = useCallback(() => {
    onClick?.(pil.id);
  }, [onClick, pil.id]);

  useEffect(() => {
    const marker = markerRef.current;
    if (!marker || !isDraggable || !onDragEnd) return;

    const listener = marker.addListener("dragend", () => {
      const pos = marker.position;
      if (pos) {
        const lat =
          typeof pos.lat === "function"
            ? (pos as google.maps.LatLng).lat()
            : (pos as google.maps.LatLngLiteral).lat;
        const lng =
          typeof pos.lng === "function"
            ? (pos as google.maps.LatLng).lng()
            : (pos as google.maps.LatLngLiteral).lng;
        onDragEnd(lat, lng);
      }
    });

    return () => {
      listener.remove();
    };
  }, [isDraggable, onDragEnd]);

  const size = isSelected ? 32 : 26;

  return (
    <AdvancedMarker
      ref={(marker) => {
        markerRef.current = marker;
      }}
      position={{ lat: pil.lat, lng: pil.lng }}
      onClick={handleClick}
      zIndex={1200}
      draggable={isDraggable}
    >
      <div
        className="flex items-center justify-center rounded-full border-2 border-white shadow-lg cursor-pointer transition-transform hover:scale-110"
        style={{
          width: size,
          height: size,
          backgroundColor: color,
          outline: isSelected ? `3px solid ${color}55` : "none",
          outlineOffset: 2,
        }}
      >
        <MapPin size={size * 0.55} color="white" strokeWidth={2.5} />
        <span
          className="absolute -bottom-4 bg-background text-foreground border rounded px-1 text-[10px] font-semibold leading-none py-0.5"
          style={{ pointerEvents: "none" }}
        >
          {pil.ordinal}
        </span>
      </div>
    </AdvancedMarker>
  );
});
